import React, { useCallback, useEffect, useRef, useState } from "react";
import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { skillLabel } from "@/lib/constants";
import { X, Heart, Zap, MapPin, Clock, RotateCcw, Sparkles, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { ConflictWarningDialog } from "@/components/ConflictWarningDialog";

const EXIT = {
  left: { x: -560, rotate: -18, opacity: 0 },
  right: { x: 560, rotate: 18, opacity: 0 },
  up: { y: -560, opacity: 0 },
};

function OppCardBody({ o }) {
  return (
    <>
      <div className="h-28 lg:h-52 shrink-0 bg-forest/10 overflow-hidden">
        {o.cover_image ? (
          <img src={imgSrc(o.cover_image)} alt={o.title} className="w-full h-full object-cover" draggable={false} />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-forest to-forest/70 flex items-center justify-center">
            <Sparkles className="w-10 h-10 text-white/40" />
          </div>
        )}
      </div>
      <div className="p-4 lg:p-6 flex-1 min-h-0 overflow-hidden">
        <div className="text-[10px] lg:text-xs uppercase tracking-[0.18em] text-terracotta/80 font-bold">{o.org_name}</div>
        <div className="font-display text-lg lg:text-2xl text-forest mt-0.5 lg:mt-1 leading-tight line-clamp-2">{o.title}</div>
        {o.community_led && (
          <Badge className="mt-1.5 lg:mt-2 rounded-full text-[10px] bg-terracotta text-white border-none">Community-led</Badge>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 lg:mt-3 text-xs lg:text-sm text-neutral-600">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-forest/60" />{o.remote ? "Remote" : o.location}{!o.remote && o.distance_km != null ? ` · ${o.distance_km} km` : ""}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-forest/60" />{o.hours_required}h</span>
        </div>
        <p className="text-xs lg:text-sm text-neutral-600 mt-2 lg:mt-3 line-clamp-2 lg:line-clamp-3">{o.description}</p>
        <div className="flex flex-wrap gap-1.5 mt-2 lg:mt-4">
          {(o.matched_values || o.values || []).slice(0, 3).map(v => (
            <Badge key={v} variant="outline" className="rounded-full text-[11px] capitalize border-forest/20 text-forest">{v}</Badge>
          ))}
          {(o.matched_skills || o.skills_needed || []).slice(0, 3).map(s => (
            <Badge key={s} className="rounded-full text-[11px] capitalize bg-terracotta/10 text-terracotta border-none">{skillLabel(s)}</Badge>
          ))}
          {o.match_score > 0 && (
            <Badge className="rounded-full text-[11px] bg-forest text-white border-none ml-auto"><Sparkles className="w-3 h-3 mr-1" />Match</Badge>
          )}
        </div>
      </div>
    </>
  );
}

function CandidateCardBody({ c }) {
  const v = c.volunteer;
  return (
    <div className="p-4 lg:p-6 flex-1 min-h-0 overflow-hidden">
      <div className="flex items-center gap-3 lg:gap-4">
        <Avatar className="w-12 h-12 lg:w-16 lg:h-16 border border-forest/10">
          <AvatarImage src={imgSrc(v.picture)} alt={v.name} />
          <AvatarFallback className="bg-forest text-white text-lg lg:text-xl">{v.name?.[0]}</AvatarFallback>
        </Avatar>
        <div>
          <div className="font-display text-lg lg:text-2xl text-forest leading-tight">{v.name}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{v.location || "Location not set"} · {v.total_hours}h volunteered</div>
        </div>
      </div>
      <Badge className={`mt-3 lg:mt-4 rounded-full border-none ${c.source === "applied" ? "bg-forest text-white" : "bg-terracotta/10 text-terracotta"}`}>
        {c.source === "applied" ? "Applied to" : "Interested in"}: {c.opportunity_title}
      </Badge>
      {v.bio && <p className="text-xs lg:text-sm text-neutral-600 mt-3 lg:mt-4 line-clamp-2 lg:line-clamp-3">{v.bio}</p>}
      <div className="flex flex-wrap gap-1.5 mt-3 lg:mt-4">
        {(v.skills || []).slice(0, 5).map(s => (
          <Badge key={s} className={`rounded-full text-[11px] capitalize border-none ${(c.matched_skills || []).includes(s) ? "bg-forest text-white" : "bg-neutral-100 text-neutral-600"}`}>
            {skillLabel(s)}
          </Badge>
        ))}
      </div>
      {(c.matched_skills?.length > 0 || c.matched_values?.length > 0) && (
        <div className="text-[11px] text-forest/70 mt-3 font-medium inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> {c.matched_skills.length + c.matched_values.length} matching skills & values
        </div>
      )}
    </div>
  );
}

function OppCardBack({ o }) {
  return (
    <div data-testid={`swipe-card-back-${o.id}`} className="p-4 lg:p-6 flex flex-col h-full min-h-0">
      <div className="text-[10px] lg:text-xs uppercase tracking-[0.18em] text-terracotta/80 font-bold">About this opportunity</div>
      <div className="font-display text-lg lg:text-2xl text-forest mt-0.5 leading-tight line-clamp-2">{o.title}</div>
      <div className="mt-1 text-xs lg:text-sm text-neutral-500">
        by{" "}
        <Link data-testid={`card-back-org-link-${o.id}`} to={`/o/${o.org_user_id}`} className="text-forest font-medium hover:underline">
          {o.org_name}
        </Link>
      </div>
      <div className="mt-2 lg:mt-3 flex-1 min-h-0 overflow-y-auto pr-1 text-xs lg:text-sm text-neutral-600 whitespace-pre-line">
        {o.description}
      </div>
      <div className="pt-2 mt-2 border-t border-neutral-100 flex items-center justify-between shrink-0">
        <Link data-testid={`card-back-opp-link-${o.id}`} to={`/opportunities/${o.id}`} className="text-xs text-forest font-medium hover:underline">
          View full page →
        </Link>
        <span className="text-[10px] text-neutral-400">Tap card to flip back</span>
      </div>
    </div>
  );
}

function CandidateCardBack({ c }) {
  const v = c.volunteer;
  return (
    <div data-testid={`swipe-card-back-${c.target_id}`} className="p-4 lg:p-6 flex flex-col h-full min-h-0">
      <div className="text-[10px] lg:text-xs uppercase tracking-[0.18em] text-terracotta/80 font-bold">About this volunteer</div>
      <div className="font-display text-lg lg:text-2xl text-forest mt-0.5 leading-tight">{v.name}</div>
      <div className="mt-1 text-xs text-neutral-500">{v.location || "Location not set"} · {v.total_hours}h volunteered</div>
      <div className="mt-2 lg:mt-3 flex-1 min-h-0 overflow-y-auto pr-1 text-xs lg:text-sm text-neutral-600">
        <p className="whitespace-pre-line">{v.bio || "No bio yet."}</p>
        {(v.skills || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {v.skills.map(s => (
              <Badge key={s} className="rounded-full text-[11px] capitalize bg-neutral-100 text-neutral-600 border-none">{skillLabel(s)}</Badge>
            ))}
          </div>
        )}
      </div>
      <div className="pt-2 mt-2 border-t border-neutral-100 flex items-center justify-between shrink-0">
        <Link data-testid={`card-back-profile-link-${c.target_id}`} to={`/u/${v.user_id}`} className="text-xs text-forest font-medium hover:underline">
          View profile →
        </Link>
        <span className="text-[10px] text-neutral-400">Tap card to flip back</span>
      </div>
    </div>
  );
}

function SwipeCard({ item, isOrg, onSwipe, active, allowUp = true }) {
  // F&F batch (2026-02): tap-to-flip. The back face carries the FULL
  // description + org/profile link. Tap-vs-drag disambiguation is manual
  // (pointer down/up distance < 10px within 600ms = tap) so real swipes
  // never mis-trigger a flip and taps never fire a swipe (onDragEnd's own
  // 130px thresholds already ignore sub-tap movements). While flipped,
  // drag is disabled so the long description can scroll on touch — the
  // action buttons below still swipe.
  const [flipped, setFlipped] = useState(false);
  const press = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-250, 250], [-14, 14]);
  const likeOpacity = useTransform(x, [40, 140], [0, 1]);
  const nopeOpacity = useTransform(x, [-140, -40], [1, 0]);
  const upOpacity = useTransform(y, [-140, -40], [1, 0]);

  const onDragEnd = (_, info) => {
    if (info.offset.y < -130 && Math.abs(info.offset.x) < 130) return allowUp ? onSwipe("up") : undefined;
    if (info.offset.x > 130) return onSwipe("right");
    if (info.offset.x < -130) return onSwipe("left");
  };

  const onPointerDown = (e) => { press.current = { x: e.clientX, y: e.clientY, t: Date.now() }; };
  const onPointerUp = (e) => {
    const p = press.current;
    press.current = null;
    if (!p || !active) return;
    if (e.target.closest("a, button")) return; // links on the back face navigate, not flip
    if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < 10 && Date.now() - p.t < 600) {
      setFlipped(f => !f);
    }
  };

  return (
    <motion.div
      data-testid={`swipe-card-${isOrg ? item.target_id : item.id}`}
      className="absolute inset-0 bg-white rounded-2xl border border-neutral-200 shadow-xl overflow-hidden select-none"
      style={{ x, y, rotate, cursor: active ? "grab" : "default" }}
      drag={active && !flipped}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.9}
      onDragEnd={onDragEnd}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      initial={{ scale: 0.96, opacity: 0.8 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={(EXIT[item._exitDir] || EXIT.left)}
      transition={{ duration: 0.28 }}
      whileTap={active && !flipped ? { cursor: "grabbing" } : undefined}
    >
      {active && !flipped && (
        <>
          <motion.div style={{ opacity: likeOpacity }} className="absolute top-6 left-6 z-10 rotate-[-12deg] border-4 border-forest text-forest font-display text-2xl px-4 py-1 rounded-lg bg-white/80">
            {isOrg ? "SHORTLIST" : "SAVE"}
          </motion.div>
          <motion.div style={{ opacity: nopeOpacity }} className="absolute top-6 right-6 z-10 rotate-[12deg] border-4 border-red-400 text-red-500 font-display text-2xl px-4 py-1 rounded-lg bg-white/80">
            SKIP
          </motion.div>
          {allowUp && (
            <motion.div style={{ opacity: upOpacity }} className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10 border-4 border-terracotta text-terracotta font-display text-2xl px-4 py-1 rounded-lg bg-white/80">
              APPLY
            </motion.div>
          )}
        </>
      )}
      <div className="w-full h-full [perspective:1400px]">
        <div className={`relative w-full h-full transition-transform duration-500 [transform-style:preserve-3d] ${flipped ? "[transform:rotateY(180deg)]" : ""}`}>
          <div className="absolute inset-0 flex flex-col overflow-hidden bg-white [backface-visibility:hidden] [-webkit-backface-visibility:hidden]">
            {isOrg ? <CandidateCardBody c={item} /> : <OppCardBody o={item} />}
            {active && (
              <div data-testid="card-flip-hint" className="absolute bottom-1.5 left-1/2 -translate-x-1/2 text-[10px] text-neutral-400 bg-white/85 border border-neutral-100 rounded-full px-2.5 py-0.5 pointer-events-none">
                Tap for details
              </div>
            )}
          </div>
          <div className="absolute inset-0 flex flex-col overflow-hidden bg-white [transform:rotateY(180deg)] [backface-visibility:hidden] [-webkit-backface-visibility:hidden]">
            {isOrg ? <CandidateCardBack c={item} /> : <OppCardBack o={item} />}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Discover() {
  const { user } = useAuth();
  const isOrg = user?.user_type === "organisation";
  const [deck, setDeck] = useState([]);
  const [saved, setSaved] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [lastSwipe, setLastSwipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conflictData, setConflictData] = useState(null); // A1: {conflicts, severity, proceed}
  const busy = useRef(false);

  const loadDeck = useCallback(() => {
    setLoading(true);
    const p = isOrg ? api.orgSwipeDeck() : api.swipeDeck();
    p.then(setDeck).catch(() => setDeck([])).finally(() => setLoading(false));
    api.savedSwipes().then(setSaved).catch(() => {});
    api.skippedSwipes().then(setSkipped).catch(() => {});
  }, [isOrg]);

  useEffect(() => { if (user) loadDeck(); }, [user, loadDeck]);

  // A1: swipe-up applies instantly, so the double-booking check runs BEFORE
  // the card animates away; the dialog's confirm resumes the original action.
  const doSwipe = async (dir) => {
    if (busy.current || deck.length === 0) return;
    if (dir === "up" && !isOrg && deck[0]?.event_date) {
      try {
        const c = await api.myConflicts({ date: deck[0].event_date.slice(0, 10), exclude_id: deck[0].id });
        if (c.conflicts?.length) return setConflictData({ ...c, proceed: () => performSwipe("up") });
      } catch { /* never block the swipe on the check */ }
    }
    performSwipe(dir);
  };

  const performSwipe = async (dir) => {
    if (busy.current || deck.length === 0) return;
    setConflictData(null);
    busy.current = true;
    const top = deck[0];
    const targetId = isOrg ? top.target_id : top.id;
    setDeck(d => [{ ...d[0], _exitDir: dir }, ...d.slice(1)]);
    requestAnimationFrame(() => setDeck(d => d.slice(1)));
    try {
      const res = await api.swipe(targetId, dir);
      setLastSwipe({ item: top, dir });
      if (res.match) toast.success("It's a match! You're both interested — say hello in Messages.", { duration: 5000 });
      else if (dir === "left") toast(isOrg ? "Passed — we'll show them again in 30 days" : "Skipped — find it in your Skipped tab");
      else if (dir === "right") toast.success(isOrg ? "Shortlisted" : "Saved to your shortlist");
      else if (res.accepted) toast.success(`Accepted ${top.volunteer?.name}'s application!`);
      else if (res.invited) toast.success(`Invitation sent to ${top.volunteer?.name}`);
      else if (res.requires_attestation) {
        // B4: requirement-gated roles — attestation happens on the detail page
        toast("This role has requirements — saved to your shortlist. Open it to confirm and apply.", { duration: 5000 });
        api.savedSwipes().then(setSaved).catch(() => {});
      }
      else if (res.applied) toast.success("Application sent!");
      if (dir === "right") api.savedSwipes().then(setSaved).catch(() => {});
      if (dir === "left") api.skippedSwipes().then(setSkipped).catch(() => {});
      if (res.first_application) window.dispatchEvent(new Event("civic:first-application"));
    } catch {
      toast.error("Something went wrong");
    } finally {
      busy.current = false;
    }
  };

  const undo = async () => {
    if (!lastSwipe || busy.current) return;
    busy.current = true;
    try {
      await api.swipeUndo();
      const { _exitDir, ...item } = lastSwipe.item;
      setDeck(d => [item, ...d.filter(x => (isOrg ? x.target_id : x.id) !== (isOrg ? item.target_id : item.id))]);
      if (lastSwipe.dir === "right") api.savedSwipes().then(setSaved).catch(() => {});
      if (lastSwipe.dir === "left") api.skippedSwipes().then(setSkipped).catch(() => {});
      setLastSwipe(null);
      toast("Swipe undone");
    } catch {
      toast.error("Nothing to undo");
    } finally {
      busy.current = false;
    }
  };

  const restore = async (targetId) => {
    await api.unsave(targetId);
    setSkipped(s => s.filter(x => (isOrg ? x.target_id : x.id) !== targetId));
    loadDeck();
    toast("Restored — it's back in your deck");
  };

  const unsave = async (targetId) => {
    await api.unsave(targetId);
    setSaved(s => s.filter(x => (isOrg ? x.target_id : x.id) !== targetId));
    toast("Removed from shortlist");
  };

  const applySaved = async (o) => {
    // A1: same double-booking check on the Saved-tab apply button.
    if (o.event_date) {
      try {
        const c = await api.myConflicts({ date: o.event_date.slice(0, 10), exclude_id: o.id });
        if (c.conflicts?.length) return setConflictData({ ...c, proceed: () => performApplySaved(o) });
      } catch { /* non-blocking */ }
    }
    performApplySaved(o);
  };

  const performApplySaved = async (o) => {
    setConflictData(null);
    try {
      const res = await api.apply(o.id);
      setSaved(s => s.map(x => x.id === o.id ? { ...x, applied: true } : x));
      toast.success("Application sent!");
      if (res?.first_application) window.dispatchEvent(new Event("civic:first-application"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply"); }
  };

  const acceptSaved = async (c) => {
    try {
      const res = await api.swipe(c.target_id, "up");
      setSaved(s => s.filter(x => x.target_id !== c.target_id));
      toast.success(res.accepted ? "Application accepted!" : "Invitation sent!");
    } catch { toast.error("Something went wrong"); }
  };

  if (!user) return null;

  return (
    /* Mobile (<lg, tab bar present): the page becomes a fixed-height flex
       column — 100dvh minus navbar (4rem) and the .App tab-bar padding
       (4.5rem + safe area) — so header, card and action buttons ALL fit one
       viewport and swiping never fights page scroll. Desktop (lg+) keeps the
       original block layout. */
    <div
      data-testid="discover-page"
      className="max-w-3xl mx-auto px-4 sm:px-6 pt-3 pb-1 lg:py-10 flex flex-col lg:block h-[calc(100dvh-4rem-4.5rem-env(safe-area-inset-bottom))] lg:h-auto"
    >
      <Seo title="Quick match" />
      <ConflictWarningDialog
        open={!!conflictData}
        onOpenChange={(o) => !o && setConflictData(null)}
        conflicts={conflictData?.conflicts || []}
        severity={conflictData?.severity}
        onConfirm={() => conflictData?.proceed?.()}
      />
      <div className="text-center shrink-0">
        <div className="hidden lg:block text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Quick match</div>
        <h1 className="font-display text-2xl lg:text-4xl text-forest lg:mt-2 tracking-tight">
          {isOrg ? "Swipe through candidates" : "Swipe through causes"}
        </h1>
        <p className="hidden lg:block text-neutral-600 mt-2 text-sm">
          {isOrg
            ? "Drag left to pass · right to shortlist · tap a card for full details"
            : "Drag left to skip · right to save · up to apply · tap a card for full details"}
        </p>
        <p data-testid="discover-swipe-legend" className="lg:hidden text-[11px] text-neutral-500 mt-0.5">
          {isOrg ? "← pass · shortlist → · tap = details" : "← skip · save → · ↑ apply · tap = details"}
        </p>
      </div>

      <Tabs defaultValue="deck" className="mt-2 lg:mt-8 flex-1 min-h-0 flex flex-col lg:block">
        <TabsList className="mx-auto flex w-fit rounded-full h-8 lg:h-10 shrink-0">
          <TabsTrigger data-testid="discover-tab-deck" value="deck" className="rounded-full px-4 lg:px-6 text-xs lg:text-sm">Deck</TabsTrigger>
          <TabsTrigger data-testid="discover-tab-saved" value="saved" className="rounded-full px-4 lg:px-6 text-xs lg:text-sm">
            {isOrg ? "Shortlist" : "Saved"} ({saved.length})
          </TabsTrigger>
          <TabsTrigger data-testid="discover-tab-skipped" value="skipped" className="rounded-full px-4 lg:px-6 text-xs lg:text-sm">
            Skipped ({skipped.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="deck" className="flex-1 min-h-0 flex flex-col lg:block">
          <div className="relative flex-1 min-h-0 w-full max-w-md mx-auto mt-2 lg:mt-6 lg:h-[540px] lg:flex-none">
            {loading && <div className="absolute inset-0 flex items-center justify-center text-neutral-500 text-sm">Shuffling the deck…</div>}
            {!loading && deck.length === 0 && (
              <div data-testid="deck-empty" className="absolute inset-0 flex flex-col items-center justify-center text-center bg-white rounded-2xl border border-dashed border-neutral-300 p-8">
                <Sparkles className="w-10 h-10 text-forest/30" />
                <div className="font-display text-xl text-forest mt-4">You're all caught up</div>
                <p className="text-sm text-neutral-500 mt-2 max-w-xs">
                  {isOrg ? "No new candidates right now. Post more opportunities to attract volunteers." : "No new opportunities to swipe. Skipped cards return after 30 days."}
                </p>
                <Button data-testid="deck-refresh" variant="outline" className="rounded-full mt-5" onClick={loadDeck}>
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Refresh
                </Button>
              </div>
            )}
            <AnimatePresence>
              {deck.slice(0, 3).reverse().map((item, idx, arr) => (
                <SwipeCard
                  key={isOrg ? item.target_id : item.id}
                  item={item}
                  isOrg={isOrg}
                  active={idx === arr.length - 1}
                  onSwipe={doSwipe}
                  allowUp={!isOrg}
                />
              ))}
            </AnimatePresence>
          </div>

          {(deck.length > 0 || lastSwipe) && (
            <div className="flex items-center justify-center gap-4 lg:gap-5 mt-3 lg:mt-8 shrink-0">
              <button data-testid="swipe-undo-btn" onClick={undo} disabled={!lastSwipe} aria-label="Undo last swipe"
                className={`w-10 h-10 lg:w-11 lg:h-11 rounded-full bg-white border border-neutral-200 shadow-md flex items-center justify-center transition-transform ${lastSwipe ? "text-amber-500 hover:scale-110" : "text-neutral-300 cursor-not-allowed"}`}>
                <RotateCcw className="w-4 h-4 lg:w-5 lg:h-5" />
              </button>
              <button data-testid="swipe-left-btn" onClick={() => doSwipe("left")} aria-label="Skip"
                className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-white border border-neutral-200 shadow-md flex items-center justify-center text-red-500 hover:scale-110 transition-transform">
                <X className="w-5 h-5 lg:w-6 lg:h-6" />
              </button>
              {!isOrg && (
                <button data-testid="swipe-up-btn" onClick={() => doSwipe("up")} aria-label="Apply"
                  className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-terracotta text-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform">
                  <Zap className="w-6 h-6 lg:w-7 lg:h-7" />
                </button>
              )}
              <button data-testid="swipe-right-btn" onClick={() => doSwipe("right")} aria-label={isOrg ? "Shortlist" : "Save"}
                className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-white border border-neutral-200 shadow-md flex items-center justify-center text-forest hover:scale-110 transition-transform">
                <Heart className="w-5 h-5 lg:w-6 lg:h-6" />
              </button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="saved" className="flex-1 min-h-0 overflow-y-auto lg:overflow-visible">
          <div className="mt-6 space-y-3 max-w-xl mx-auto" data-testid="saved-list">
            {saved.length === 0 && (
              <div className="text-center text-sm text-neutral-500 py-12">Nothing {isOrg ? "shortlisted" : "saved"} yet — swipe right on cards you like.</div>
            )}
            {!isOrg && saved.map(o => (
              <div key={o.id} data-testid={`saved-item-${o.id}`} className="bg-white border border-neutral-200 rounded-xl p-5 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <Link to={`/opportunities/${o.id}`} className="font-medium text-forest hover:underline block truncate">{o.title}</Link>
                  <div className="text-xs text-neutral-500 mt-0.5">{o.org_name} · {o.location}</div>
                </div>
                {o.applied ? (
                  <Badge className="bg-forest/10 text-forest border-none rounded-full shrink-0">Applied</Badge>
                ) : (
                  <Button data-testid={`saved-apply-${o.id}`} size="sm" onClick={() => applySaved(o)} className="rounded-full bg-forest hover:bg-forest-hover h-8 text-xs shrink-0">Apply</Button>
                )}
                <button data-testid={`saved-remove-${o.id}`} onClick={() => unsave(o.id)} className="text-neutral-400 hover:text-red-500 shrink-0" aria-label="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {isOrg && saved.map(c => (
              <div key={c.target_id} data-testid={`saved-item-${c.target_id}`} className="bg-white border border-neutral-200 rounded-xl p-5 flex items-center gap-4">
                <Avatar className="w-10 h-10 shrink-0">
                  <AvatarImage src={imgSrc(c.volunteer.picture)} alt={c.volunteer.name} />
                  <AvatarFallback className="bg-forest text-white">{c.volunteer.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <Link to={`/u/${c.volunteer.user_id}`} className="font-medium text-forest hover:underline block truncate">{c.volunteer.name}</Link>
                  <div className="text-xs text-neutral-500 mt-0.5 truncate">{c.opportunity_title}</div>
                </div>
                <Button size="sm" onClick={() => acceptSaved(c)} className="rounded-full bg-forest hover:bg-forest-hover h-8 text-xs shrink-0">
                  <UserCheck className="w-3.5 h-3.5 mr-1" /> Accept
                </Button>
                <button onClick={() => unsave(c.target_id)} className="text-neutral-400 hover:text-red-500 shrink-0" aria-label="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="skipped" className="flex-1 min-h-0 overflow-y-auto lg:overflow-visible">
          <div className="mt-6 space-y-3 max-w-xl mx-auto" data-testid="skipped-list">
            {skipped.length === 0 && (
              <div className="text-center text-sm text-neutral-500 py-12">Nothing skipped. Cards you swipe left land here for 30 days, just in case.</div>
            )}
            {!isOrg && skipped.map(o => (
              <div key={o.id} data-testid={`skipped-item-${o.id}`} className="bg-white border border-neutral-200 rounded-xl p-5 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <Link to={`/opportunities/${o.id}`} className="font-medium text-forest hover:underline block truncate">{o.title}</Link>
                  <div className="text-xs text-neutral-500 mt-0.5">{o.org_name} · {o.location}</div>
                </div>
                <Button data-testid={`skipped-restore-${o.id}`} size="sm" variant="outline" onClick={() => restore(o.id)} className="rounded-full h-8 text-xs shrink-0">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                </Button>
              </div>
            ))}
            {isOrg && skipped.map(c => (
              <div key={c.target_id} data-testid={`skipped-item-${c.target_id}`} className="bg-white border border-neutral-200 rounded-xl p-5 flex items-center gap-4">
                <Avatar className="w-10 h-10 shrink-0">
                  <AvatarImage src={imgSrc(c.volunteer.picture)} alt={c.volunteer.name} />
                  <AvatarFallback className="bg-forest text-white">{c.volunteer.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <Link to={`/u/${c.volunteer.user_id}`} className="font-medium text-forest hover:underline block truncate">{c.volunteer.name}</Link>
                  <div className="text-xs text-neutral-500 mt-0.5 truncate">{c.opportunity_title}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => restore(c.target_id)} className="rounded-full h-8 text-xs shrink-0">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
